import React, { useState, useEffect } from 'react';
const API_URL = process.env.REACT_APP_API_URL;

// Helper to get valid admin access token, refresh if needed
async function getAdminAccessToken() {
  let accessToken = localStorage.getItem('gogig_admin_access');
  const refreshToken = localStorage.getItem('gogig_admin_refresh');
  if (accessToken) {
    try {
      const res = await fetch(`${API_URL}/api/admin/stats`, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      if (res.status !== 401) return accessToken;
    } catch {}
  }
  if (refreshToken) {
    try {
      const res = await fetch(`${API_URL}/api/admin/refresh-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken })
      });
      const data = await res.json();
      if (data.success && data.accessToken) {
        localStorage.setItem('gogig_admin_access', data.accessToken);
        return data.accessToken;
      }
    } catch {}
  }
  localStorage.removeItem('gogig_admin_access');
  localStorage.removeItem('gogig_admin_refresh');
  window.location.href = '/admin';
  return null;
}

async function adminFetch(url, options = {}) {
  const token = await getAdminAccessToken();
  if (!token) throw new Error('Not authenticated');
  const headers = { ...(options.headers || {}), Authorization: `Bearer ${token}` };
  return fetch(url, { ...options, headers });
}

function AdminSPOCUploadStatusReport({ apiBase, getToken, fetch: fetchProp }) {
  const getTokenFn = getToken || getAdminAccessToken;
  const fetchFn = fetchProp || adminFetch;
  const base = apiBase ? `${API_URL}/${apiBase}` : `${API_URL}/api/admin`;
  const uploadStatusPath = 'spoc-upload-status';
  const currentDate = new Date();
  const [year, setYear] = useState(currentDate.getFullYear());
  const [month, setMonth] = useState(currentDate.getMonth() + 1);
  const [report, setReport] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expandedSpocs, setExpandedSpocs] = useState(new Set());

  const fetchReport = async () => {
    if (!year || !month) {
      setError('Please select both year and month');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const res = await fetchFn(`${base}/${uploadStatusPath}?year=${year}&month=${month}`);
      const data = await res.json();
      if (data.success) {
        setReport(data.report);
        setSummary(data.summary);
        // Expand all SPOCs by default
        setExpandedSpocs(new Set(data.report.map(spoc => spoc.spoc_id)));
      } else {
        setError(data.message || 'Failed to fetch report');
      }
    } catch (err) {
      setError('Failed to fetch report');
      console.error(err);
    }
    setLoading(false);
  };

  useEffect(() => {
    // Auto-fetch on mount with current month/year
    fetchReport();
  }, []);

  const toggleSpoc = (spocId) => {
    const newExpanded = new Set(expandedSpocs);
    if (newExpanded.has(spocId)) {
      newExpanded.delete(spocId);
    } else {
      newExpanded.add(spocId);
    }
    setExpandedSpocs(newExpanded);
  };

  const handleExportCSV = () => {
    if (!report.length) return;

    let csv = 'SPOC Name,SPOC Email,Company Name,Establishment Code,Uploaded\n';

    report.forEach(spocData => {
      if (spocData.companies.length === 0) {
        csv += `"${spocData.spoc_name}","${spocData.spoc_email}",No Companies Assigned,,\n`;
      } else {
        spocData.companies.forEach(company => {
          csv += `"${spocData.spoc_name}","${spocData.spoc_email}","${company.company_name}","${company.establishment_code || ''}",${company.uploaded}\n`;
        });
      }
    });

    // Add summary row
    if (summary) {
      csv += `\nSUMMARY,,,,Total: ${summary.total_companies}, Uploaded: ${summary.uploaded}, Not Uploaded: ${summary.not_uploaded}\n`;
    }

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `spoc_upload_status_${year}_${String(month).padStart(2, '0')}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // Generate year options (current year, previous 2 years, and next 2 years)
  const currentYear = new Date().getFullYear();
  const yearOptions = [];
  // Add previous 2 years
  for (let i = 2; i > 0; i--) {
    yearOptions.push(currentYear - i);
  }
  // Add current year
  yearOptions.push(currentYear);
  // Add next 2 years
  for (let i = 1; i <= 2; i++) {
    yearOptions.push(currentYear + i);
  }

  return (
    <div style={{ maxWidth: 1400, margin: '0 auto', padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h2 style={{ fontWeight: 800, fontSize: 28, color: '#5a4ff3', margin: 0 }}>
          SPOC Upload Status Report
        </h2>
        <button 
          className="gogig-btn" 
          onClick={handleExportCSV} 
          disabled={loading || !report.length}
          style={{ padding: '10px 20px' }}
        >
          Export CSV
        </button>
      </div>

      {/* Filters */}
      <div style={{ 
        background: '#f9fafb', 
        borderRadius: 12, 
        padding: 20, 
        marginBottom: 24,
        display: 'flex',
        gap: 16,
        alignItems: 'center',
        flexWrap: 'wrap'
      }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <label style={{ fontWeight: 600, color: '#333' }}>Year:</label>
          <select 
            value={year} 
            onChange={(e) => setYear(parseInt(e.target.value))}
            style={{
              padding: '8px 12px',
              borderRadius: 8,
              border: '2px solid #e1e5e9',
              fontSize: 16,
              minWidth: 100
            }}
          >
            {yearOptions.map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>

        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <label style={{ fontWeight: 600, color: '#333' }}>Month:</label>
          <select 
            value={month} 
            onChange={(e) => setMonth(parseInt(e.target.value))}
            style={{
              padding: '8px 12px',
              borderRadius: 8,
              border: '2px solid #e1e5e9',
              fontSize: 16,
              minWidth: 150
            }}
          >
            {monthNames.map((name, idx) => (
              <option key={idx + 1} value={idx + 1}>{name}</option>
            ))}
          </select>
        </div>

        <button 
          className="gogig-btn" 
          onClick={fetchReport} 
          disabled={loading}
          style={{ padding: '10px 24px' }}
        >
          {loading ? 'Loading...' : 'Fetch Report'}
        </button>
      </div>

      {loading && (
        <div style={{ textAlign: 'center', padding: 48, color: '#666' }}>
          Loading report...
        </div>
      )}

      {error && (
        <div style={{ 
          background: '#fee', 
          color: '#c33', 
          padding: 12, 
          borderRadius: 8, 
          marginBottom: 18 
        }}>
          {error}
        </div>
      )}

      {summary && !loading && (
        <div style={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: 12,
          padding: 24,
          marginBottom: 24,
          color: 'white',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
        }}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: 20, fontWeight: 700 }}>
            Summary for {monthNames[month - 1]} {year}
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
            <div>
              <div style={{ fontSize: 14, opacity: 0.9, marginBottom: 4 }}>Total Companies</div>
              <div style={{ fontSize: 28, fontWeight: 700 }}>{summary.total_companies}</div>
            </div>
            <div>
              <div style={{ fontSize: 14, opacity: 0.9, marginBottom: 4 }}>Uploaded</div>
              <div style={{ fontSize: 28, fontWeight: 700, color: '#22c55e' }}>{summary.uploaded}</div>
            </div>
            <div>
              <div style={{ fontSize: 14, opacity: 0.9, marginBottom: 4 }}>Not Uploaded</div>
              <div style={{ fontSize: 28, fontWeight: 700, color: '#ef4444' }}>{summary.not_uploaded}</div>
            </div>
            <div>
              <div style={{ fontSize: 14, opacity: 0.9, marginBottom: 4 }}>Upload Rate</div>
              <div style={{ fontSize: 28, fontWeight: 700 }}>
                {summary.total_companies > 0 
                  ? Math.round((summary.uploaded / summary.total_companies) * 100) 
                  : 0}%
              </div>
            </div>
          </div>
        </div>
      )}

      {!loading && !error && report.length === 0 && (
        <div style={{ 
          textAlign: 'center', 
          padding: 48, 
          color: '#888',
          background: '#f9fafb',
          borderRadius: 12
        }}>
          No SPOC data available for {monthNames[month - 1]} {year}.
        </div>
      )}

      {!loading && !error && report.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {report.map((spocData) => {
            const isExpanded = expandedSpocs.has(spocData.spoc_id);
            const totalCompanies = spocData.companies.length;
            const uploadedCompanies = spocData.companies.filter(c => c.uploaded === 'Yes').length;
            const notUploadedCompanies = spocData.companies.filter(c => c.uploaded === 'No').length;

            return (
              <div 
                key={spocData.spoc_id}
                style={{
                  background: '#fff',
                  borderRadius: 12,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  overflow: 'hidden'
                }}
              >
                {/* SPOC Header */}
                <div
                  onClick={() => toggleSpoc(spocData.spoc_id)}
                  style={{
                    padding: '16px 20px',
                    background: isExpanded ? '#f3f4f6' : '#fff',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderBottom: isExpanded ? '2px solid #5a4ff3' : 'none',
                    transition: 'all 0.2s'
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 18, color: '#333', marginBottom: 4 }}>
                      {spocData.spoc_name}
                    </div>
                    <div style={{ fontSize: 14, color: '#666' }}>
                      {spocData.spoc_email} • {totalCompanies} {totalCompanies === 1 ? 'Company' : 'Companies'}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 24, alignItems: 'center', marginRight: 16 }}>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 12, color: '#666', marginBottom: 2 }}>Upload Status</div>
                      <div style={{ fontSize: 16, fontWeight: 600 }}>
                        <span style={{ color: '#22c55e' }}>{uploadedCompanies} Yes</span>
                        {' / '}
                        <span style={{ color: '#ef4444' }}>{notUploadedCompanies} No</span>
                      </div>
                    </div>
                    <div style={{ fontSize: 24, color: '#5a4ff3', transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
                      ▼
                    </div>
                  </div>
                </div>

                {/* SPOC Details */}
                {isExpanded && (
                  <div style={{ padding: 20 }}>
                    {spocData.companies.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: 24, color: '#888' }}>
                        No companies assigned to this SPOC.
                      </div>
                    ) : (
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead>
                            <tr style={{ background: '#f3f4f6' }}>
                              <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, color: '#333', borderBottom: '2px solid #e5e7eb' }}>
                                Company Name
                              </th>
                              <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, color: '#333', borderBottom: '2px solid #e5e7eb' }}>
                                Establishment Code
                              </th>
                              <th style={{ padding: '12px', textAlign: 'center', fontWeight: 600, color: '#333', borderBottom: '2px solid #e5e7eb' }}>
                                Uploaded
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {spocData.companies.map((company, idx) => (
                              <tr 
                                key={company.company_id}
                                style={{ 
                                  borderBottom: idx < spocData.companies.length - 1 ? '1px solid #e5e7eb' : 'none',
                                  background: idx % 2 === 0 ? '#fff' : '#f9fafb'
                                }}
                              >
                                <td style={{ padding: '12px', fontWeight: 600, color: '#333' }}>
                                  {company.company_name}
                                </td>
                                <td style={{ padding: '12px', color: '#666', fontFamily: 'monospace' }}>
                                  {company.establishment_code || '-'}
                                </td>
                                <td style={{ padding: '12px', textAlign: 'center' }}>
                                  <span style={{
                                    padding: '4px 12px',
                                    borderRadius: 6,
                                    fontWeight: 600,
                                    fontSize: 14,
                                    background: company.uploaded === 'Yes' ? '#dcfce7' : '#fee2e2',
                                    color: company.uploaded === 'Yes' ? '#166534' : '#991b1b'
                                  }}>
                                    {company.uploaded}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default AdminSPOCUploadStatusReport;

